import React, { useRef } from 'react';
import cx, { Argument as ClassNameType } from 'classnames';

import { MDText } from 'i18n-react';

import cypressIds from 'CypressId';

import { useTrustPilotWidgetLoader } from 'Utilities/hooks/useTrustPilotWidgetLoader';
import { applyCypressData } from 'Utilities/index';
import { TRUSTPILOT_REVIEW_COUNT } from 'Utilities/reviews';

import { TRUSTPILOT_HARDCODE_RATINGS } from 'Containers/app/fastlane/trustpilot/Trustpilot.consts';
import { TrustpilotFooter } from 'Containers/app/fastlane/trustpilot/TrustpilotFooter';
import { LoadingArrow } from 'Storybook/components/local/LoadingArrow/LoadingArrow';

import LocalTexts from './TrustpilotReviewsCarouselTexts.json';

import styles from './TrustpilotReviewsCarousel.scss';

const LocalT = new MDText(LocalTexts);

const t = (key: string, args?: Record<string, string>) =>
  `${LocalT.translate(key, args)}`;

const texts = {
  readMore: t('readMore'),
  title: t('title', { reviewCount: TRUSTPILOT_REVIEW_COUNT }),
} as const;

type TrustpilotReviewsCarouselHTMLProps = {
  className?: ClassNameType | string;
  failedLoad?: boolean;
  largeTitle?: boolean;
  onLoad?: () => void;
  ref?: React.RefObject<HTMLDivElement | null>;
  setFailedLoad?: (value: boolean) => void;
  subTitle?: React.ReactNode;
  title?: React.ReactNode;
};

const TrustpilotReviewsCarouselHTML = React.memo((
  {
    className,
    failedLoad,
    largeTitle,
    ref,
    subTitle,
    title = texts.title,
  }: TrustpilotReviewsCarouselHTMLProps,
) => {
  if (failedLoad) {
    return null;
  }

  if (TRUSTPILOT_HARDCODE_RATINGS) {
    return <div className={cx(styles.component, className)}>
      {title && <h2 className={cx({ [styles.largeTitle]: largeTitle })}>{title}</h2>}
      {subTitle && <p>{subTitle}</p>}
      <div
        ref={ref}
        {...applyCypressData(cypressIds.sections.trustPilotReviews)}
      >
        <TrustpilotFooter />
      </div>
    </div>;
  }

  return (
    <div className={cx(styles.component, className)}>
      {title && <h2 className={cx({ [styles.largeTitle]: largeTitle })}>{title}</h2>}
      {subTitle && <p>{subTitle}</p>}
      <div
        ref={ref}
        className="trustpilot-widget"
        data-businessunit-id="59f1a7fc0000ff0005aeb29e"
        data-locale="en-GB"
        data-review-languages="en"
        data-style-height="240px"
        data-style-width="100%"
        data-template-id="54ad5defc6454f065c28af8b"
        data-testid="trustpilot-widget-reviews"
        data-theme="light"
        id="trustpilot-widget-reviews"
        {...applyCypressData(cypressIds.sections.trustPilotReviews)}
      >
        <LoadingArrow />
      </div>
    </div>
  );
}, ({ failedLoad: prevValue }, { failedLoad: newValue }) => prevValue === newValue);

export const TrustpilotReviewsCarousel = ({ onLoad, setFailedLoad, ...props }: TrustpilotReviewsCarouselHTMLProps) => {
  const $container = useRef<HTMLDivElement>(null);
  const observerSettings = {
    disconnectOnVisible: true,
    rootMargin: '500px',
  };

  useTrustPilotWidgetLoader({ $container, observerSettings, onLoad, setFailedLoad });

  return (<TrustpilotReviewsCarouselHTML {...props} ref={$container} />);
};
